/**
 * The Issues page's "Capture Issues" control: ONE primary action that opens
 * the team review at once, plus a caret menu holding the one escape hatch,
 * "Capture a different page" (the URL prompt).
 *
 * Why a split button and not two buttons: Carbon and GOV.UK both allow one
 * primary per screen, and this toolbar already carries an outline sibling
 * ("Create Client Link"). Measured on production 2026-09-10 (90 days, external,
 * created 30+ minutes after signup): 38 of 65 deliberate team reviews opened
 * on the project's own root, 13 on a deeper page of the same site (reachable
 * from the review's URL bar), 7 on another site, 7 on a project with no
 * domain. The caret is for the 7 + the deep-page user who wants to type it
 * first; the primary is for everyone else.
 *
 * Pending copy matches the modal's own submit ("Opening...").
 */
import { ArrowDown01Icon } from '@hugeicons/core-free-icons';
import { RowActionMenu } from '@/components/shared/RowActionMenu';
import { captureIssuesLabel } from '@/lib/captureIssuesUrl';

interface CaptureIssuesButtonProps {
  onCapture: () => void;
  onCaptureOther: () => void;
  pending: boolean;
  /**
   * The page the primary action will open, so the button can NAME it instead of
   * making the user press it to find out (Emily, 2026-09-10, D2 option C).
   * null when nothing is known, and the label falls back to the shared
   * "Capture Issues" vocabulary the other surfaces use.
   */
  targetUrl?: string | null;
}

export const CAPTURE_OTHER_PAGE_LABEL = 'Capture a different page';

export function CaptureIssuesButton({ onCapture, onCaptureOther, pending, targetUrl }: CaptureIssuesButtonProps) {
  const label = captureIssuesLabel(targetUrl);
  return (
    <div className="inline-flex items-stretch min-w-0">
      <button
        type="button"
        // min-w-0 + truncate: the label now carries a URL, so the primary
        // ABSORBS the squeeze instead of wrapping to two lines. Measured on the
        // mini 2026-09-10: without this, a full-length label wrapped BOTH this
        // button and "Create Client Link" at 1280px (pre-fix frame: both single
        // line), a regression the row-alignment assertion could not see.
        className="btn-primary btn-toolbar flex items-center gap-2 rounded-r-none min-w-0 max-w-[18rem] whitespace-nowrap"
        onClick={onCapture}
        disabled={pending}
        aria-busy={pending || undefined}
        title={targetUrl ? `Opens ${targetUrl}` : undefined}
      >
        <span className="truncate">{pending ? 'Opening...' : label}</span>
        {!pending && (
          <svg className="shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M7 17L17 7M17 7H7M17 7v10" />
          </svg>
        )}
      </button>
      <RowActionMenu<null>
        item={null}
        label="More capture options"
        actions={[{ label: CAPTURE_OTHER_PAGE_LABEL, onClick: onCaptureOther }]}
        triggerIcon={ArrowDown01Icon}
        triggerClassName="btn-primary btn-toolbar rounded-l-none px-2 border-l border-white/25"
        triggerDisabled={pending}
      />
    </div>
  );
}
